import React from "react";

import { Grid } from "@material-ui/core";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { updateModalContext } from "actions";

import { MasterRuleSetAction } from "actions/ruleSets";

import { Button, SecondaryButton } from "components/Form";
import { Input, SelectField } from "components/Form/FormikControls";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { ModalTypes } from "constants/navigation";

import {
  convertToExternalCriterion,
  convertToExternalInputField,
} from "containers/Settings/RuleBooks/export";
import { convertToInternalRuleSet } from "containers/Settings/RuleBooks/import";
import { CriteriaForm } from "containers/Settings/RuleBooks/Rules/CustomRules/Criteria/CriteriaForm";
import { useRuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";

import {
  getContextByModalType,
  getIsStaff,
  getMasterRuleBooks,
  getMasterRuleById,
  getMasterRuleSetById,
} from "selectors";

import { useIsMobile } from "hooks";

export const MasterRuleSetForm = ({ isCreate }) => {
  const selectableRuleBooks = useSelector(getMasterRuleBooks);
  const ruleBooks = Object.values(selectableRuleBooks).map(rb => ({
    value: rb.id,
    label: rb.name,
  }));

  return (
    <Grid container>
      <Grid item xs={12}>
        <Input label="Name" name="name" required />
      </Grid>
      <Grid item xs={12}>
        <CriteriaForm parentInputPath={null} />
      </Grid>
      {isCreate && (
        <SelectField
          label="Rule Book"
          name="rule_book_id"
          options={ruleBooks}
          required
        />
      )}
    </Grid>
  );
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Please enter a Rule Set name"),
});

export function MasterRuleSetModal(props) {
  const { onClose } = props;

  const context =
    useSelector(getContextByModalType(ModalTypes.EditMasterRuleSet)) || {};
  const {
    masterRuleId,
    masterRuleSetId = null,
    newRuleSet,
    previousModalContextKey,
  } = context;

  const masterRule = useSelector(getMasterRuleById(masterRuleId));

  const isCreate = !masterRuleSetId;

  const masterRuleSet =
    useSelector(getMasterRuleSetById(masterRuleSetId)) || newRuleSet || {};

  const isStaff = useSelector(getIsStaff);

  const readOnly = !isStaff;

  const title = `${isCreate ? "Create" : readOnly ? "View" : "Edit"} Master Rule Set`;

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  function onSubmit(values, formikProps) {
    values.input_field = convertToExternalInputField(
      `$.${values.input_field.value}`,
    );
    values.criteria = values.criteria.map(convertToExternalCriterion);
    if (isCreate) {
      const id = uuidv4();
      const payload = {
        ...values,
        rule_book_id: masterRule.rule_book_id,
        id,
      };

      dispatch(MasterRuleSetAction.create(payload));
      if (previousModalContextKey) {
        dispatch(
          updateModalContext(previousModalContextKey, {
            ruleSetId: id,
          }),
        );
      }
    } else {
      dispatch(MasterRuleSetAction.update(values));
      dispatch(
        updateModalContext(previousModalContextKey, {
          ruleSetId: values.id,
        }),
      );
    }
    handleClose(formikProps);
  }

  const schema = useRuleFieldSchema();

  const initialValues = convertToInternalRuleSet({ ...masterRuleSet }, schema);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {formikProps => {
          return (
            <Form>
              <DialogContent dividers>
                <MasterRuleSetForm />
              </DialogContent>
              <DialogActions>
                <SecondaryButton
                  onClick={() => handleClose(formikProps)}
                  type="button"
                >
                  Back
                </SecondaryButton>
                <Button
                  data-tour={isCreate ? "create" : "save"}
                  type="submit"
                  disabled={Object.keys(formikProps.errors).length || readOnly}
                >
                  {isCreate ? "Create" : "Save"}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
